import { useCallback, useEffect, useState } from "react";
import { fetchTreasuryReserves, fetchTreasurySnapshot } from "../services/api.js";

/**
 * Reads Treasury reserve buckets + solvency snapshot via the backend.
 *
 * Per data-flow spec: chain → backend → frontend. The frontend never
 * reads the Treasury contract directly. If the backend is unreachable
 * the hook surfaces an error rather than falling through to chain so
 * we never silently diverge between two sources of truth.
 */
export function useTreasury({ pollMs = 30_000 } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // `opts.background === true` = silent poll refresh: leave the current
    // figures on screen while re-fetching so the cards never flip back to
    // shimmer (the periodic layout jitter). Only the first foreground call
    // toggles loading / clears data on error.
    const refresh = useCallback(async (opts = {}) => {
        const background = opts.background === true;
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const reserves = await fetchTreasuryReserves();
            const snapshot = await fetchTreasurySnapshot().catch(() => null);
            setData({
                totalAllocated: BigInt(reserves.totalAllocated || "0"),
                interestReserve: BigInt(reserves.interestReserve || "0"),
                buybackReserve: BigInt(reserves.buybackReserve || "0"),
                operationalReserve: BigInt(reserves.operationalReserve || "0"),
                outstandingLoanPrincipal: BigInt(reserves.outstandingLoanPrincipal || "0"),
                observedAt: snapshot?.observedAt || reserves.observedAt || null,
                source: reserves.source || "api"
            });
            setError(null);
        } catch (err) {
            setError(err?.message || "Failed to fetch Treasury data");
            if (!background) setData(null);
        } finally {
            if (!background) setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(() => refresh({ background: true }), pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { data, isLoading, error, refresh };
}
